<template>
    <div class="mb-3 flex items-center justify-end gap-2">
        <button
            v-if="activeFiltersCount > 0"
            @click="clearFilters"
            class="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
        >
            Limpar filtros
        </button>
        <button
            @click="filtersOpen = true"
            class="inline-flex items-center gap-2 rounded bg-indigo-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-400"
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-7.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
            </svg>
            Filtros
            <span
                v-if="activeFiltersCount > 0"
                class="ml-1 rounded-full bg-white px-1.5 py-0.5 text-xs font-bold text-indigo-600"
            >
                {{ activeFiltersCount }}
            </span>
        </button>
    </div>

    <table-component
        :headers="[
            'Quem alterou',
            'Ação',
            'Permissões afetadas',
            'Motivo',
            'Data',
        ]"
        :loading="loading"
        :pagination="pagination"
        v-model="pagination"
        @fetch="fetch"
    >
        <tr
            v-for="log in history"
            :key="log.id"
            class="border-b border-slate-200 align-middle text-slate-700 last:border-b-0 dark:border-slate-700 dark:text-slate-200"
        >
            <td class="min-w-[220px] whitespace-nowrap px-3">
                <template v-if="log.causer">
                    {{ log.causer.id }} - {{ log.causer.name }}
                </template>
                <span v-else class="italic text-slate-400">—</span>
            </td>
            <td class="px-3">
                <span
                    v-if="log.granted && log.granted.length"
                    class="mr-1 inline-block rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800"
                >
                    Adicionou
                </span>
                <span
                    v-if="log.revoked && log.revoked.length"
                    class="inline-block rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800"
                >
                    Removeu
                </span>
            </td>
            <td class="px-3 py-2">
                <div
                    v-if="log.granted && log.granted.length"
                    class="flex flex-wrap gap-1"
                >
                    <span
                        v-for="perm in log.granted"
                        :key="`g-${perm.name}`"
                        class="inline-flex items-center rounded bg-green-50 px-2 py-0.5 text-xs text-green-800 dark:bg-green-900/30 dark:text-green-300"
                        :title="perm.name"
                    >
                        <span class="mr-1 text-green-600">+</span>
                        {{ perm.title }}
                    </span>
                </div>
                <div
                    v-if="log.revoked && log.revoked.length"
                    class="mt-1 flex flex-wrap gap-1"
                >
                    <span
                        v-for="perm in log.revoked"
                        :key="`r-${perm.name}`"
                        class="inline-flex items-center rounded bg-red-50 px-2 py-0.5 text-xs text-red-800 dark:bg-red-900/30 dark:text-red-300"
                        :title="perm.name"
                    >
                        <span class="mr-1 text-red-600">−</span>
                        {{ perm.title }}
                    </span>
                </div>
            </td>
            <td class="px-3">
                <span v-if="log.reason">{{ log.reason }}</span>
                <span v-else class="italic text-slate-400">—</span>
            </td>

            <td class="px-3">{{ log.created_at }}</td>
        </tr>
    </table-component>

    <TransitionRoot as="template" :show="filtersOpen">
        <Dialog as="div" class="relative z-50" @close="filtersOpen = false">
            <TransitionChild
                as="template"
                enter="ease-in-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in-out duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-slate-900/50 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-hidden">
                <div class="absolute inset-0 overflow-hidden">
                    <div
                        class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full"
                    >
                        <TransitionChild
                            as="template"
                            enter="transform transition ease-in-out duration-300"
                            enter-from="translate-x-full"
                            enter-to="translate-x-0"
                            leave="transform transition ease-in-out duration-300"
                            leave-from="translate-x-0"
                            leave-to="translate-x-full"
                        >
                            <DialogPanel
                                class="pointer-events-auto w-screen max-w-md"
                            >
                                <div
                                    class="flex h-full flex-col bg-white shadow-xl dark:bg-slate-800"
                                >
                                    <div
                                        class="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700"
                                    >
                                        <DialogTitle
                                            class="text-lg font-semibold text-slate-900 dark:text-slate-100"
                                        >
                                            Filtros
                                        </DialogTitle>
                                        <button
                                            @click="filtersOpen = false"
                                            class="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                class="h-5 w-5"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                                stroke-width="2"
                                            >
                                                <path
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    d="M6 18L18 6M6 6l12 12"
                                                />
                                            </svg>
                                        </button>
                                    </div>


                                    <div
                                        class="flex-1 overflow-y-auto px-4 py-4"
                                    >

                                    <div class="mb-4">
                                            <label
                                                class="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200"
                                            >
                                                Data inicial
                                            </label>
                                            <input
                                                type="date"
                                                v-model="draft.dateFrom"
                                                class="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                                            />
                                        </div>

                                        <div class="mb-4">
                                            <label
                                                class="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200"
                                            >
                                                Data final
                                            </label>
                                            <input
                                                type="date"
                                                v-model="draft.dateTo"
                                                class="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                                            />
                                        </div>

                                        <div class="mb-4">
                                            <label
                                                class="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200"
                                            >
                                                Ação
                                            </label>
                                            <select
                                                v-model="draft.action"
                                                class="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                                            >
                                                <option value="">Todas</option>
                                                <option value="granted">
                                                    Adicionadas
                                                </option>
                                                <option value="revoked">
                                                    Removidas
                                                </option>
                                            </select>
                                        </div>



                                    </div>

                                    <div
                                        class="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-3 dark:border-slate-700"
                                    >
                                        <button
                                            @click="clearDraft"
                                            class="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                                        >
                                            Limpar
                                        </button>
                                        <button
                                            @click="applyFilters"
                                            class="rounded bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-400"
                                        >
                                            Aplicar
                                        </button>
                                    </div>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import TableComponent from "../../../components/layouts/table/TableComponent.vue";

const emptyFilters = () => ({
    action: "",
    permissionType: "",
    source: "",
    dateFrom: "",
    dateTo: "",
});

export default {
    props: ["user"],
    components: {
        TableComponent,
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
    },

    data() {
        return {
            history: [],
            loading: false,
            filtersOpen: false,
            applied: emptyFilters(),
            draft: emptyFilters(),

            pagination: {
                total: 0,
                per_page: 15,
                current_page: 1,
                last_page: 1,
                from: 1,
                to: 1,
            },
        };
    },

    computed: {
        activeFiltersCount() {
            return Object.values(this.applied).filter((v) => v !== "").length;
        },
    },

    mounted() {
        this.fetch();
    },

    methods: {
        fetch() {
            this.loading = true;
            this.history = [];

            const params = { page: this.pagination.current_page };
            if (this.applied.action) {
                params["filter[action]"] = this.applied.action;
            }
            if (this.applied.permissionType) {
                params["filter[permission_type]"] = this.applied.permissionType;
            }
            if (this.applied.source) {
                params["filter[source]"] = this.applied.source;
            }
            if (this.applied.dateFrom) {
                params["filter[date_from]"] = this.applied.dateFrom;
            }
            if (this.applied.dateTo) {
                params["filter[date_to]"] = this.applied.dateTo;
            }

            axios
                .get(`/api/user/${this.user.id}/permission/history`, { params })
                .then(({ data }) => {
                    this.history = data.data;
                    this.pagination = data.meta || data;
                    this.loading = false;
                })
                .catch(() => {
                    this.loading = false;
                });
        },
        applyFilters() {
            this.applied = { ...this.draft };
            this.pagination.current_page = 1;
            this.filtersOpen = false;
            this.fetch();
        },
        clearDraft() {
            this.draft = emptyFilters();
        },
        clearFilters() {
            this.applied = emptyFilters();
            this.draft = emptyFilters();
            this.pagination.current_page = 1;
            this.fetch();
        },
        sourceLabel(source) {
            const labels = {
                panel: "Painel",
                api: "API",
            };
            return labels[source] || source || "—";
        },
        sourceBadgeClass(source) {
            const classes = {
                panel: "bg-indigo-100 text-indigo-800",
                api: "bg-amber-100 text-amber-800",
            };
            return classes[source] || "bg-slate-100 text-slate-600";
        },
    },

    watch: {
        filtersOpen(open) {
            if (open) {
                this.draft = { ...this.applied };
            }
        },
    },
};
</script>
